import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Box, Button } from "@mui/material";
import { InlineIcon } from "@iconify/react";
import { CREATE_APPROVAL_BY_USER } from "my-time/graphql/mutations";
import { useMyTimeUserApproval } from "my-time/hooks/use-my-time-user-approval";
import { useOcSnackbar } from "oc/hooks";
import { OcConfirmationDialog } from "oc/components";
import useRecordingPeriod from "my-time/hooks/use-recording-period/use-recording-period";

interface MyTimeApprovalUserCloseProps {
  formattedDate: string;
  allTimesNull?: boolean;
}

export const MyTimeApprovalUserClose = ({ formattedDate, allTimesNull }: MyTimeApprovalUserCloseProps) => {
  const { t } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { data: recordingPeriodData } = useRecordingPeriod();

  const { isApproved, refetch, loading } = useMyTimeUserApproval();
  const { setOpenMessage } = useOcSnackbar();

  const [createApprovalByUser] = useMutation(CREATE_APPROVAL_BY_USER, {
    onCompleted: () => {
      setOpenMessage({
        message: t("MY_TIME_APPROVAL_USER_CLOSE_SUCCESS"),
        type: "success",
      });
      refetch();
    },
    onError: () => {
      setOpenMessage({
        message: t("MY_TIME_APPROVAL_USER_CLOSE_ERROR"),
        type: "error",
      });
    },
  });

  const handleButtonClick = async () => {
    await createApprovalByUser();
    setShowConfirmDialog(false);
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const isButtonDisabled = Boolean(
    isApproved ||
    loading ||
    (recordingPeriodData && formattedDate !== recordingPeriodData)
    || allTimesNull
  );

  const buttonText =
    isApproved && !loading
      ? t("MY_TIME_CLOSED_MONTH")
      : t("MY_TIME_CLOSE_MONTH");

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleButtonClick}
        onCancel={handleDialogCancel}
        title={t("MY_TIME_CONFIRM_CLOSE_MONTH_TITLE")}
        texts={[t("MY_TIME_CONFIRM_CLOSE_MONTH_TEXT")]}
        okButtonText={t("MY_TIME_CLOSE_MONTH")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="flex-end"
        padding="16px 0px"
      >
        <Button
          onClick={() => setShowConfirmDialog(true)}
          startIcon={<InlineIcon icon="mdi:lock-outline" />}
          disabled={isButtonDisabled}
          aria-disabled={isButtonDisabled}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
};

export default MyTimeApprovalUserClose;
